import React, { useState, useMemo } from 'react';
import { Target } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput } from '../../components/calculators/MeasureInputs';
import { GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import {
  idealWeightDevine, idealWeightHamwi, idealWeightRobinson, idealWeightMiller, getHealthyWeightRange
} from '../../lib/calculatorUtils';

const IdealWeightCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [gender, setGender] = useState('female');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    if (!h || h < 120) return null;
    return {
      devine: idealWeightDevine(gender, h),
      hamwi: idealWeightHamwi(gender, h),
      robinson: idealWeightRobinson(gender, h),
      miller: idealWeightMiller(gender, h),
      bmiRange: getHealthyWeightRange(h)
    };
  }, [heightCm, gender]);

  const avg = result ? +((result.devine + result.hamwi + result.robinson + result.miller) / 4).toFixed(1) : null;

  const inputs = (
    <div className="space-y-5" data-testid="iw-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Details</h2>
      <p className="text-slate-400 text-sm mb-5">Devine, Hamwi, Robinson & Miller formulas, plus BMI-based healthy range.</p>
      <GenderField value={gender} onChange={setGender} testIdPrefix="iw-gender" />
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Average Ideal Weight" value={avg} unit="kg" accent="from-emerald-500 to-blue-500" testId="iw-result-average" />
      <div className="mt-8 space-y-1">
        <StatRow label="Devine formula" value={result.devine} unit="kg" testId="iw-devine" />
        <StatRow label="Hamwi formula" value={result.hamwi} unit="kg" testId="iw-hamwi" />
        <StatRow label="Robinson formula" value={result.robinson} unit="kg" testId="iw-robinson" />
        <StatRow label="Miller formula" value={result.miller} unit="kg" testId="iw-miller" />
        <StatRow label="Healthy BMI range" value={`${result.bmiRange.min} – ${result.bmiRange.max}`} unit="kg" color="text-emerald-300" testId="iw-bmi-range" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Ideal-weight formulas estimate a population-level target. Body composition matters more than weight alone for athletes and muscular individuals.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your height and gender to see your ideal weight estimates." />
  );

  return (
    <CalculatorLayout
      slug="ideal-weight" title="Ideal Weight Calculator"
      subtitle="Estimate a healthy body weight range using Devine, Hamwi, Robinson & Miller formulas."
      metaDescription="Ideal Weight Calculator using Devine, Hamwi, Robinson & Miller formulas plus BMI-based healthy weight range."
      icon={Target}
      accentGradient="from-emerald-500 to-blue-500" accentColor="text-emerald-400"
      badgeBg="bg-emerald-500/10" badgeBorder="border-emerald-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Devine:</strong> Male 50 + 2.3 × (in over 5ft) · Female 45.5 + 2.3 × (in over 5ft)</p>
        <p><strong className="text-slate-300">Hamwi:</strong> Male 48 + 2.7 × (in over 5ft) · Female 45.5 + 2.2 × (in over 5ft)</p>
      </>}
    />
  );
};

export default IdealWeightCalculatorPage;
